import { createApi } from '@reduxjs/toolkit/query/react';

import {
  CrmTasksEndpoints as Endpoints,
  type CompletedCrmTaskRequest,
  type CompletedCrmTaskResponse,
  type CreateCrmTaskCommentRequest,
  type CreateCrmTaskCommentResponse,
  type CreateCrmTaskRequest,
  type CreateCrmTaskResponse,
  type CreateCrmTaskTypeRequest,
  type CreateCrmTaskTypeResponse,
  type DeleteCrmTaskByIdRequest,
  type DeleteCrmTaskByIdResponse,
  type DeleteCrmTaskTypeRequest,
  type DeleteCrmTaskTypeResponse,
  type DeleteCrmTasksRequest,
  type DeleteCrmTasksResponse,
  type EditCrmTaskTypeRequest,
  type EditCrmTaskTypeResponse,
  type GetCrmTaskByIdRequest,
  type GetCrmTaskByIdResponse,
  type GetCrmTaskFilterDataRequest,
  type GetCrmTaskFilterDataResponse,
  type GetCrmTaskHistoryByIdRequest,
  type GetCrmTaskHistoryByIdResponse,
  type GetCrmTaskTypeByIdRequest,
  type GetCrmTaskTypeByIdResponse,
  type GetCrmTaskTypesRequest,
  type GetCrmTaskTypesResponse,
  type GetCrmTasksForPeriodRequest,
  type GetCrmTasksForPeriodResponse,
  type GetSearchCrmTasksRequest,
  type GetSearchCrmTasksResponse,
  type InProgressCrmTaskRequest,
  type InProgressCrmTaskResponse,
  type SearchCrmTaskClientsRequest,
  type SearchCrmTaskClientsResponse,
  type SearchCrmTaskUsersRequest,
  type SearchCrmTaskUsersResponse,
  type SearchCrmTasksRequest,
  type SearchCrmTasksResponse,
  type SetCompletedForCrmTasksRequest,
  type SetCompletedForCrmTasksResponse,
  type SetOwnersForCrmTasksRequest,
  type SetOwnersForCrmTasksResponse,
  type UpdateCrmTaskCommentRequest,
  type UpdateCrmTaskCommentResponse,
  type UpdateCrmTaskRequest,
  type UpdateCrmTaskResponse,
} from '@app/api/CrmTasks/types';
import { createApiBaseQuery, Methods } from '@app/api/shared';

export const crmTasksApi = createApi({
  reducerPath: 'crmTasksApi2',
  baseQuery: createApiBaseQuery('/CrmTasks'),
  endpoints: ({ mutation, query }) => ({
    [Endpoints.GetCrmTaskById]: query<
      GetCrmTaskByIdResponse,
      GetCrmTaskByIdRequest
    >({
      query: ({ id, ...params }) => ({
        url: `${id}`,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.DeleteCrmTaskById]: mutation<
      DeleteCrmTaskByIdResponse,
      DeleteCrmTaskByIdRequest
    >({
      query: ({ id, ...params }) => ({
        url: `${id}`,
        method: Methods.DELETE,
        params,
      }),
    }),
    [Endpoints.CreateCrmTask]: mutation<
      CreateCrmTaskResponse,
      CreateCrmTaskRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.CreateCrmTask,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.UpdateCrmTask]: mutation<
      UpdateCrmTaskResponse,
      UpdateCrmTaskRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateCrmTask,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.CompletedCrmTask]: mutation<
      CompletedCrmTaskResponse,
      CompletedCrmTaskRequest
    >({
      query: ({ id, ...params }) => ({
        url: `${Endpoints.CompletedCrmTask}/${id}`,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.InProgressCrmTask]: mutation<
      InProgressCrmTaskResponse,
      InProgressCrmTaskRequest
    >({
      query: ({ id, ...params }) => ({
        url: `${Endpoints.InProgressCrmTask}/${id}`,
        method: Methods.POST,
        params,
      }),
    }),
    [Endpoints.GetCrmTasksForPeriod]: mutation<
      GetCrmTasksForPeriodResponse,
      GetCrmTasksForPeriodRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.GetCrmTasksForPeriod,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.SearchCrmTasks]: mutation<
      SearchCrmTasksResponse,
      SearchCrmTasksRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SearchCrmTasks,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetSearchCrmTasks]: query<
      GetSearchCrmTasksResponse,
      GetSearchCrmTasksRequest
    >({
      query: (params) => ({
        url: Endpoints.GetSearchCrmTasks,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SetOwnersForCrmTasks]: mutation<
      SetOwnersForCrmTasksResponse,
      SetOwnersForCrmTasksRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetOwnersForCrmTasks,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.SetCompletedForCrmTasks]: mutation<
      SetCompletedForCrmTasksResponse,
      SetCompletedForCrmTasksRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SetCompletedForCrmTasks,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteCrmTasks]: mutation<
      DeleteCrmTasksResponse,
      DeleteCrmTasksRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.DeleteCrmTasks,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetCrmTaskFilterData]: query<
      GetCrmTaskFilterDataResponse,
      GetCrmTaskFilterDataRequest
    >({
      query: (params) => ({
        url: Endpoints.GetCrmTaskFilterData,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SearchCrmTaskClients]: query<
      SearchCrmTaskClientsResponse,
      SearchCrmTaskClientsRequest
    >({
      query: (params) => ({
        url: Endpoints.SearchCrmTaskClients,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SearchCrmTaskUsers]: query<
      SearchCrmTaskUsersResponse,
      SearchCrmTaskUsersRequest
    >({
      query: (params) => ({
        url: Endpoints.SearchCrmTaskUsers,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.CreateCrmTaskComment]: mutation<
      CreateCrmTaskCommentResponse,
      CreateCrmTaskCommentRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.CreateCrmTaskComment,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.UpdateCrmTaskComment]: mutation<
      UpdateCrmTaskCommentResponse,
      UpdateCrmTaskCommentRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.UpdateCrmTaskComment,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetCrmTaskHistoryById]: query<
      GetCrmTaskHistoryByIdResponse,
      GetCrmTaskHistoryByIdRequest
    >({
      query: ({ id, ...params }) => ({
        url: `${Endpoints.GetCrmTaskHistoryById}/${id}`,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.CreateCrmTaskType]: mutation<
      CreateCrmTaskTypeResponse,
      CreateCrmTaskTypeRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.CreateCrmTaskType,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.EditCrmTaskType]: mutation<
      EditCrmTaskTypeResponse,
      EditCrmTaskTypeRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.EditCrmTaskType,
        // Backend declares this endpoint as HttpPut; POST returns 500.
        method: Methods.PUT,
        params,
        data,
      }),
    }),
    [Endpoints.DeleteCrmTaskType]: mutation<
      DeleteCrmTaskTypeResponse,
      DeleteCrmTaskTypeRequest
    >({
      query: ({ id, ...params }) => ({
        url: `${Endpoints.DeleteCrmTaskType}/${id}`,
        method: Methods.DELETE,
        params,
      }),
    }),
    [Endpoints.GetCrmTaskTypeById]: query<
      GetCrmTaskTypeByIdResponse,
      GetCrmTaskTypeByIdRequest
    >({
      query: ({ id, ...params }) => ({
        url: `${Endpoints.GetCrmTaskTypeById}/${id}`,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetCrmTaskTypes]: query<
      GetCrmTaskTypesResponse,
      GetCrmTaskTypesRequest
    >({
      query: (params) => ({
        url: Endpoints.GetCrmTaskTypes,
        method: Methods.GET,
        params,
      }),
    }),
  }),
});

export const {
  useGetCrmTaskByIdQuery,
  useLazyGetCrmTaskByIdQuery,
  useDeleteCrmTaskByIdMutation,
  useCreateCrmTaskMutation,
  useUpdateCrmTaskMutation,
  useCompletedCrmTaskMutation,
  useInProgressCrmTaskMutation,
  useGetCrmTasksForPeriodMutation,
  useSearchCrmTasksMutation,
  useGetSearchCrmTasksQuery,
  useLazyGetSearchCrmTasksQuery,
  useSetOwnersForCrmTasksMutation,
  useSetCompletedForCrmTasksMutation,
  useDeleteCrmTasksMutation,
  useGetCrmTaskFilterDataQuery,
  useSearchCrmTaskClientsQuery,
  useLazySearchCrmTaskClientsQuery,
  useSearchCrmTaskUsersQuery,
  useLazySearchCrmTaskUsersQuery,
  useCreateCrmTaskCommentMutation,
  useUpdateCrmTaskCommentMutation,
  useGetCrmTaskHistoryByIdQuery,
  useLazyGetCrmTaskHistoryByIdQuery,
  useCreateCrmTaskTypeMutation,
  useEditCrmTaskTypeMutation,
  useDeleteCrmTaskTypeMutation,
  useGetCrmTaskTypeByIdQuery,
  useGetCrmTaskTypesQuery,
  useLazyGetCrmTaskTypesQuery,
} = crmTasksApi;
